import { useEffect, useRef, useState } from 'react';
import {
  CompanyManagementSummary,
  getCompanyCalendarEvents,
  getCompanyManagementSummary,
} from 'services/actions/representant_dashboard';
import { companyName } from 'services/selected_companies';
import { CalendarEvent } from 'store/modules/management_store/types';
import { RepresentedTypes } from 'store/modules/profile_store/types';
import { CompanyCount, companyCounts } from './useRepresentantDashboard';

export type ManagedChartMetric = 'favoritadas' | 'gerenciadas' | 'finalizadas' | 'ganhas';

export const MANAGED_CHART_METRICS: ManagedChartMetric[] = [
  'favoritadas',
  'gerenciadas',
  'finalizadas',
  'ganhas',
];

export interface CompanyCalendarEvent extends CalendarEvent {
  companyId: number;
  company: string;
}

const COMPANY_REQUESTS = 3;

const chartValue = (summary: CompanyManagementSummary, metric: ManagedChartMetric): number => {
  const finished = summary.total_finished || {};
  switch (metric) {
    case 'favoritadas':
      return summary.total_favorites || 0;
    case 'gerenciadas':
      return summary.total_managed || 0;
    case 'finalizadas':
      return Object.values(finished).reduce((sum: number, value) => sum + (Number(value) || 0), 0);
    default:
      return finished.winner || 0;
  }
};

async function perCompany<T>(
  ids: number[],
  load: (id: number) => Promise<T>,
  isActive: () => boolean,
  onBatch?: (results: Partial<Record<number, T | null>>) => void,
): Promise<Partial<Record<number, T | null>>> {
  const results: Partial<Record<number, T | null>> = {};
  for (let start = 0; start < ids.length && isActive(); start += COMPANY_REQUESTS) {
    const batch = ids.slice(start, start + COMPANY_REQUESTS);
    // eslint-disable-next-line no-await-in-loop
    const values = await Promise.all(batch.map((id) => load(id).catch(() => null)));
    batch.forEach((id, index) => {
      results[id] = values[index];
    });
    if (onBatch && isActive()) onBatch({ ...results });
  }
  return results;
}

export function useRepresentantManagedOverview(companies: RepresentedTypes[], scope: string) {
  const enabled = companies.length > 1;
  const companiesKey = companies.map(({ id }) => id).join(',');
  const [summaries, setSummaries] = useState<
    Partial<Record<number, CompanyManagementSummary | null>>
  >({});
  const [summariesLoaded, setSummariesLoaded] = useState(false);
  const [events, setEvents] = useState<CompanyCalendarEvent[]>([]);
  const lastEvents = useRef(0);

  useEffect(() => {
    if (!enabled) return undefined;
    let active = true;
    setSummaries({});
    setSummariesLoaded(false);
    perCompany(
      companiesKey.split(',').map(Number),
      (id) => getCompanyManagementSummary(id, scope),
      () => active,
      (partial) => setSummaries(partial),
    ).then(() => active && setSummariesLoaded(true));

    return () => {
      active = false;
    };
  }, [enabled, companiesKey, scope]);

  const loadEvents = (start: string, end: string): Promise<boolean> => {
    lastEvents.current += 1;
    const request = lastEvents.current;
    const ids = companiesKey.split(',').map(Number);

    return perCompany(
      ids,
      (id) => getCompanyCalendarEvents(id, scope, start, end),
      () => request === lastEvents.current,
    ).then((results) => {
      if (request !== lastEvents.current) return false;
      setEvents(
        companies.flatMap((company) =>
          (results[company.id] || []).map((event) => ({
            ...event,
            id: `${company.id}-${event.id}`,
            companyId: company.id,
            company: companyName(company),
          })),
        ),
      );
      return ids.some((id) => results[id]);
    });
  };

  const breakdown = (metric: ManagedChartMetric): CompanyCount[] =>
    companyCounts(companies, (id) => {
      const summary = summaries[id];
      return summary ? chartValue(summary, metric) : summary;
    });

  const chart = Object.fromEntries(
    MANAGED_CHART_METRICS.map((metric) => [metric, breakdown(metric)]),
  ) as Record<ManagedChartMetric, CompanyCount[]>;

  return {
    enabled,
    chart,
    totalManaged: chart.gerenciadas.reduce((sum, item) => sum + (item.value || 0), 0),
    summariesLoaded,
    events,
    loadEvents,
  };
}
